import { useMemo } from 'react'
import { addDaysInTz, weekRangeInTz } from '../lib/calendar-time'
import { BuildingCalendarTimeGridView } from './BuildingCalendarTimeGridView'
import { useTranslation } from '../i18n'
import type { BuildingCalendarResponse } from '@hub-wifi/shared'

interface BuildingCalendarWeekViewProps {
  /** Any date inside the desired week (YYYY-MM-DD in the building's timezone). */
  date: string
  /** The week-range calendar payload (already fetched by the parent page). */
  data: BuildingCalendarResponse
  loading?: boolean
}

/**
 * Week-view renderer for the building calendar.
 *
 * Thin wrapper over the shared `BuildingCalendarTimeGridView` with 7 day
 * columns (Mon..Sun in the building's timezone). All grid logic, adaptive
 * hours, chip rendering, and tooltip behavior live in the shared component.
 */
export function BuildingCalendarWeekView({ date, data }: BuildingCalendarWeekViewProps) {
  const { t } = useTranslation()
  const timezone = data.buildingTimezone
  const { mondayYmd } = weekRangeInTz(date, timezone)
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysInTz(mondayYmd, i, timezone)),
    [mondayYmd, timezone],
  )

  // Hide Sat/Sun columns when the week has no weekend events. Most weeks are
  // weekday-only for office bookings; rendering 7 columns wastes horizontal
  // space and crams the weekday chips. We check both startAt and endAt so a
  // Fri→Sat overnight event still surfaces the weekend columns.
  const visibleDays = useMemo(() => {
    const satYmd = days[5]
    const sunYmd = days[6]
    if (!satYmd || !sunYmd) return days
    const dayFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const touchesWeekend = (iso: string): boolean => {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return false
      const ymd = dayFmt.format(d)
      return ymd === satYmd || ymd === sunYmd
    }
    for (const room of data.rooms) {
      for (const ev of room.events) {
        if (touchesWeekend(ev.startAt) || touchesWeekend(ev.endAt)) return days
      }
    }
    return days.slice(0, 5)
  }, [days, data.rooms, timezone])

  return (
    <BuildingCalendarTimeGridView
      days={visibleDays}
      data={data}
      ariaLabel={t('buildingCalendar.weekGridLabel')}
      gridTestId="calendar-week-grid"
    />
  )
}
